/**
 * @fullvsl/app-shell/router — hash-based router for the app shell (FUL-225).
 *
 * Hash-based routing so the app works on any static host (Cloudflare Pages,
 * GitHub Pages, file://) without server-side rewrite rules. The iframe context
 * in Full Funnel means history-API navigation can be restricted, but hash
 * changes always work.
 *
 * Routes:
 *   #/                        → VSL list
 *   #/new                     → create new VSL
 *   #/vsl/:id                 → VSL config editor
 *   #/vsl/:id/preview         → player preview
 *   #/vsl/:id/analytics       → per-VSL analytics dashboard
 *   #/vsl/:id/publish         → finalize deployment (embed + integration)
 *   #/analytics               → global analytics overview (FUL-272)
 *   #/templates               → overlay template gallery (FUL-272)
 *   #/settings                → legacy alias for the VSL list
 *   #/support                 → help & support (FUL-272)
 *   #/integrations            → Full Funnel self-service connection screen (FUL-254)
 *
 * Every sidebar menu item resolves to a real route (FUL-272): no menu link is a
 * dead-end "not found". Unknown paths still fall through to the notFound view.
 */

export type Route =
  | { name: "list" }
  | { name: "create" }
  | { name: "editor"; id: string }
  | { name: "preview"; id: string }
  | { name: "analytics"; id: string }
  | { name: "publish"; id: string }
  | { name: "analyticsOverview" }
  | { name: "templates" }
  | { name: "support" }
  | { name: "signout" }
  | { name: "integrations" }
  | { name: "notFound"; path: string };

export interface Router {
  /** Current resolved route. */
  current(): Route;
  /** Navigate to a path (updates location.hash). */
  navigate(path: string): void;
  /** Subscribe to route changes. Returns an unsubscribe function. */
  subscribe(fn: (route: Route) => void): () => void;
}

export function parseHash(hash: string): Route {
  const clean = hash.replace(/^#/, "");
  const parts = clean.split("/").filter(Boolean);
  if (parts.length === 0) return { name: "list" };
  if (parts.length === 1 && parts[0] === "new") return { name: "create" };
  if (parts.length === 1 && parts[0] === "integrations") return { name: "integrations" };
  if (parts.length === 1 && parts[0] === "analytics") return { name: "analyticsOverview" };
  if (parts.length === 1 && parts[0] === "templates") return { name: "templates" };
  if (parts.length === 1 && parts[0] === "settings") return { name: "list" };
  if (parts.length === 1 && parts[0] === "support") return { name: "support" };
  if (parts.length === 1 && parts[0] === "signout") return { name: "signout" };
  if (parts.length >= 2 && parts[0] === "vsl") {
    const id = parts[1];
    if (parts.length === 2) return { name: "editor", id };
    if (parts.length === 3 && parts[2] === "preview") return { name: "preview", id };
    if (parts.length === 3 && parts[2] === "analytics") return { name: "analytics", id };
    if (parts.length === 3 && parts[2] === "publish") return { name: "publish", id };
  }
  return { name: "notFound", path: clean };
}

export function createRouter(): Router {
  const listeners = new Set<(route: Route) => void>();
  let currentRoute = parseHash(location.hash);

  function fire(): void {
    currentRoute = parseHash(location.hash);
    for (const fn of listeners) fn(currentRoute);
  }

  window.addEventListener("hashchange", fire);

  return {
    current(): Route {
      return currentRoute;
    },
    navigate(path: string): void {
      const target = path.startsWith("#") ? path : `#${path}`;
      if (location.hash === target) {
        fire();
      } else {
        location.hash = target;
      }
    },
    subscribe(fn: (route: Route) => void): () => void {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

/** Build a path string for a route (for nav links). */
export function routePath(route: Route): string {
  switch (route.name) {
    case "list":
      return "#/";
    case "create":
      return "#/new";
    case "editor":
      return `#/vsl/${route.id}`;
    case "preview":
      return `#/vsl/${route.id}/preview`;
    case "analytics":
      return `#/vsl/${route.id}/analytics`;
    case "publish":
      return `#/vsl/${route.id}/publish`;
    case "analyticsOverview":
      return "#/analytics";
    case "templates":
      return "#/templates";
    case "support":
      return "#/support";
    case "signout":
      return "#/signout";
    case "integrations":
      return "#/integrations";
    default:
      return "#/";
  }
}
